import type { LucideIcon } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { findModule } from "@/lib/modules";
import type { ModuleKey } from "@/lib/module-registry";

/** A module, as the "Lives in" line names it — its own icon and label. */
function ModuleChip({ moduleKey }: { moduleKey: ModuleKey }) {
  const mod = findModule(moduleKey);
  if (!mod) return null;
  const Icon = mod.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-elevated px-2 py-0.5 text-xs font-medium text-foreground">
      <Icon size={12} strokeWidth={1.75} className="text-primary" />
      {mod.label}
    </span>
  );
}

/**
 * One module helper: what it is, a switch, and where it lives — the modules
 * it appears in, and the pages within them.
 */
export function ModuleHelperCard({
  icon: Icon,
  title,
  description,
  modules,
  where,
  checked,
  disabled,
  onCheckedChange,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  modules: readonly ModuleKey[];
  where: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const id = `pref-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <section className="flex flex-col rounded-xl border border-border bg-card">
      <div className="flex items-start gap-4 p-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-dim text-primary">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor={id} className="text-sm font-semibold text-foreground">
            {title}
          </label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
        <span className="text-xs text-muted-foreground">Lives in</span>
        {modules.map((key) => (
          <ModuleChip key={key} moduleKey={key} />
        ))}
        <span className="text-xs text-muted-foreground">· {where}</span>
      </div>
    </section>
  );
}
